import { useState } from "react";
import { Check, Search } from "../../../components/icons";
import { Button } from "../../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Input } from "../../../components/ui/input";
import { cn } from "../../../lib/utils";
import { centsToCurrency } from "../../utils/money";
import { formatDate } from "../../utils/date";
import { api } from "../../../api";
import { toast } from "sonner";

interface InsuranceBatchDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lang: "pt" | "en";
  insuranceProviders: any[];
  pendingInsurancePayments: any[];
  onSuccess: () => void;
}

export function InsuranceBatchDrawer({
  open,
  onOpenChange,
  lang,
  insuranceProviders,
  pendingInsurancePayments,
  onSuccess,
}: InsuranceBatchDrawerProps) {
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<number[]>([]);
  const [batchAmount, setBatchAmount] = useState("");
  const [amountMode, setAmountMode] = useState<"total" | "individual">("total");
  const [individualAmounts, setIndividualAmounts] = useState<Record<number, string>>({});

  const handleCreateBatch = async () => {
    if (!selectedProviderId || selectedPaymentIds.length === 0) return;
    
    let amountCents: number;
    let paymentIdsWithAmounts: { payment_id: number; amount_cents: number }[];
    
    if (amountMode === "individual") {
      paymentIdsWithAmounts = selectedPaymentIds.map(id => {
        const amount = parseFloat(individualAmounts[id]?.replace(',', '.') || '0');
        return { payment_id: id, amount_cents: Math.round(amount * 100) };
      }).filter(p => p.amount_cents > 0);
      
      if (paymentIdsWithAmounts.length === 0) return;
      amountCents = paymentIdsWithAmounts.reduce((sum, p) => sum + p.amount_cents, 0);
    } else {
      amountCents = Math.round(parseFloat(batchAmount.replace(',', '.')) * 100);
      paymentIdsWithAmounts = [];
    }
    
    const payload: any = {
      insurance_provider_id: selectedProviderId,
      total_amount_cents: amountCents,
      payment_ids: selectedPaymentIds,
    };
    
    if (paymentIdsWithAmounts.length > 0) {
      payload.payment_amounts = paymentIdsWithAmounts;
    }
    
    const res = await api.createInsuranceBatch(payload);
    if (res.ok) {
      toast.success(lang === 'pt' ? "Lote criado com sucesso" : "Batch created successfully");
      onOpenChange(false);
      setSelectedPaymentIds([]);
      setBatchAmount("");
      setIndividualAmounts({});
      onSuccess();
    } else {
      toast.error(res.error || (lang === 'pt' ? "Erro ao criar lote" : "Error creating batch"));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-2xl flex flex-col p-0">
        <SheetHeader className="p-6 pb-2">
          <SheetTitle>{lang === 'pt' ? 'Registrar Pagamento de Convênio' : 'Register Insurance Payment'}</SheetTitle>
          <SheetDescription>
            {lang === 'pt' 
              ? 'Selecione as faturas e informe o valor total recebido'
              : 'Select the invoices and enter the total amount received'}
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto px-6 space-y-6">
          <div className="space-y-2 mt-2">
            <Label>{lang === 'pt' ? 'Convênio' : 'Insurance Provider'}</Label>
            <Select value={selectedProviderId ? String(selectedProviderId) : ''} onValueChange={v => setSelectedProviderId(Number(v))}>
              <SelectTrigger>
                <SelectValue placeholder={lang === 'pt' ? 'Selecione...' : 'Select...'} />
              </SelectTrigger>
              <SelectContent>
                {insuranceProviders.filter(p => !p.is_default).map(p => (
                  <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedProviderId ? (
            <>
              <div className="space-y-3">
                <Label className="text-[11px] font-bold uppercase tracking-tight text-neutral-500">{lang === 'pt' ? 'Forma de preenchimento' : 'Filling mode'}</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAmountMode("total")}
                    className={cn(
                      "px-3 py-1.5 rounded-lg border text-[11px] font-bold uppercase tracking-tight transition-all flex items-center gap-1.5",
                      amountMode === "total" ? "border-black bg-white ring-1 ring-black text-black" : "border-neutral-200 text-neutral-400 hover:border-neutral-300"
                    )}
                  >
                    {amountMode === "total" && <Check className="h-3 w-3" />}
                    {lang === 'pt' ? 'Valor total' : 'Total amount'}
                  </button>

                  <button
                    type="button"
                    onClick={() => setAmountMode("individual")}
                    className={cn(
                      "px-3 py-1.5 rounded-lg border text-[11px] font-bold uppercase tracking-tight transition-all flex items-center gap-1.5",
                      amountMode === "individual" ? "border-black bg-white ring-1 ring-black text-black" : "border-neutral-200 text-neutral-400 hover:border-neutral-300"
                    )}
                  >
                    {amountMode === "individual" && <Check className="h-3 w-3" />}
                    {lang === 'pt' ? 'Valor por fatura' : 'Individual amount'}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{lang === 'pt' ? 'Faturas pendentes' : 'Pending invoices'}</Label>
                <div className="max-h-80 overflow-y-auto border rounded-md">
                  <Table>
                    <TableHeader className="bg-neutral-50">
                      <TableRow>
                        <TableHead className="w-[40px]"></TableHead>
                        <TableHead className="text-xs uppercase font-bold">{lang === 'pt' ? 'Cliente' : 'Client'}</TableHead>
                        <TableHead className="text-xs uppercase font-bold">{lang === 'pt' ? 'Data' : 'Date'}</TableHead>
                        <TableHead className="text-xs uppercase font-bold text-right">{lang === 'pt' ? 'Valor' : 'Value'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const filtered = pendingInsurancePayments.filter(p => 
                          p.insurance_provider_id === selectedProviderId
                        );
                        if (filtered.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={4} className="text-sm text-neutral-500 py-8 text-center italic">
                                {lang === 'pt' 
                                  ? 'Nenhuma fatura para este convênio' 
                                  : 'No invoices for this insurance provider'}
                              </TableCell>
                            </TableRow>
                          );
                        }
                        return filtered.map(p => (
                          <TableRow 
                            key={p.id} 
                            className={`cursor-pointer transition-colors ${selectedPaymentIds.includes(p.id) ? 'bg-neutral-50' : ''}`}
                            onClick={() => {
                              if (selectedPaymentIds.includes(p.id)) {
                                setSelectedPaymentIds(prev => prev.filter(id => id !== p.id));
                              } else {
                                setSelectedPaymentIds(prev => [...prev, p.id]);
                              }
                            }}
                          >
                            <TableCell className="py-2">
                              <input
                                type="checkbox"
                                checked={selectedPaymentIds.includes(p.id)}
                                onChange={() => {}} // Controlled by row onClick
                                className="h-4 w-4 rounded border-gray-300 text-primary pointer-events-none"
                              />
                            </TableCell>
                            <TableCell className="py-2 font-medium text-sm">
                              {p.client_name}
                            </TableCell>
                            <TableCell className="py-2 text-[10px] text-neutral-500 font-bold">
                              {formatDate(p.created_at || p.due_date || p.display_date)}
                            </TableCell>
                            <TableCell className="py-2 text-right" onClick={(e) => e.stopPropagation()}>
                              {selectedPaymentIds.includes(p.id) ? (
                                amountMode === "individual" ? (
                                  <Input
                                    value={individualAmounts[p.id] || ''}
                                    onChange={e => {
                                      const val = e.target.value.replace(/\D/g, "");
                                      const amount = val ? (Number(val) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : "";
                                      setIndividualAmounts(prev => ({ ...prev, [p.id]: amount }));
                                    }}
                                    placeholder="0,00"
                                    className="w-24 h-8 text-sm text-right ml-auto"
                                  />
                                ) : batchAmount ? (
                                  <span className="text-sm font-bold">
                                    {centsToCurrency(Math.round(parseFloat(batchAmount.replace('.', '').replace(',', '.')) * 100 / selectedPaymentIds.length), lang)}
                                  </span>
                                ) : (
                                  <span className="text-sm text-neutral-400 italic">--</span>
                                )
                              ) : (
                                <span className="text-sm text-neutral-400 italic">--</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {amountMode === "total" ? (
                <div className="space-y-2 p-4 bg-neutral-50 rounded-lg border border-neutral-100">
                  <Label>{lang === 'pt' ? 'Valor total recebido (R$)' : 'Total amount received (R$)'}</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500">R$</span>
                    <Input
                      value={batchAmount}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, "");
                        const amount = val ? (Number(val) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : "";
                        setBatchAmount(amount);
                      }}
                      placeholder="0,00"
                      className="bg-white pl-9 font-bold"
                    />
                  </div>
                </div>
              ) : selectedPaymentIds.length > 0 && (
                <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-100">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
                    {lang === 'pt' ? 'Total calculado' : 'Calculated total'}
                  </p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {centsToCurrency(
                      selectedPaymentIds.reduce((sum, id) => {
                        const val = individualAmounts[id]?.replace(/\./g, "").replace(",", ".") || "0";
                        const amount = parseFloat(val);
                        return sum + (amount * 100);
                      }, 0),
                      lang
                    )}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-neutral-400 gap-2 italic">
              <Search className="h-8 w-8 opacity-20" />
              <p className="text-sm">{lang === 'pt' ? 'Selecione um convênio para continuar' : 'Select an insurance provider to continue'}</p>
            </div>
          )}
          <div className="pb-6" /> {/* Extra space at bottom of scroll */}
        </div>

        <div className="flex flex-col gap-2 p-6 border-t bg-white">
          <Button 
            className="w-full font-bold"
            onClick={handleCreateBatch} 
            disabled={
              !selectedProviderId || 
              selectedPaymentIds.length === 0 || 
              (amountMode === "total" && !batchAmount) ||
              (amountMode === "individual" && selectedPaymentIds.every(id => !individualAmounts[id]))
            }
          >
            {lang === 'pt' ? 'Confirmar Recebimento' : 'Confirm Payment'}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {lang === 'pt' ? 'Cancelar' : 'Cancel'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
